export type AspectKey = "original" | "1:1" | "4:3" | "3:2" | "16:9";

export const ASPECTS: { key: AspectKey; label: string; ratio: number | null }[] = [
  { key: "original", label: "Original", ratio: null },
  { key: "1:1", label: "Square", ratio: 1 },
  { key: "4:3", label: "4:3", ratio: 4 / 3 },
  { key: "3:2", label: "3:2", ratio: 3 / 2 },
  { key: "16:9", label: "16:9", ratio: 16 / 9 },
];

export type ProcessOptions = {
  /** Target aspect ratio (width / height). Null keeps the original framing. */
  ratio: number | null;
  /** Horizontal crop focus, 0 (left) to 1 (right). */
  offsetX?: number;
  /** Vertical crop focus, 0 (top) to 1 (bottom). */
  offsetY?: number;
  /** Longest edge in px after downscaling. */
  maxEdge?: number;
  /** JPEG quality, 0-1. */
  quality?: number;
};

export type ProcessedImage = {
  blob: Blob;
  url: string;
  width: number;
  height: number;
  bytes: number;
};

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not read that image."));
    img.src = src;
  });
}

/** Compute the source crop rectangle for a target aspect ratio. */
export function cropRect(
  width: number,
  height: number,
  ratio: number | null,
  offsetX = 0.5,
  offsetY = 0.5,
) {
  if (!ratio) return { sx: 0, sy: 0, sw: width, sh: height };
  const current = width / height;
  let sw = width;
  let sh = height;
  if (current > ratio) sw = Math.round(height * ratio);
  else sh = Math.round(width / ratio);
  const sx = Math.round((width - sw) * Math.min(Math.max(offsetX, 0), 1));
  const sy = Math.round((height - sh) * Math.min(Math.max(offsetY, 0), 1));
  return { sx, sy, sw, sh };
}

/** Scale a crop so its longest edge fits maxEdge (never upscales). */
export function fitSize(sw: number, sh: number, maxEdge: number) {
  const scale = Math.min(1, maxEdge / Math.max(sw, sh));
  return { width: Math.max(1, Math.round(sw * scale)), height: Math.max(1, Math.round(sh * scale)) };
}

export async function processImage(file: File, opts: ProcessOptions): Promise<ProcessedImage> {
  const { ratio, offsetX = 0.5, offsetY = 0.5, maxEdge = 2000, quality = 0.82 } = opts;
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImage(objectUrl);
    const { sx, sy, sw, sh } = cropRect(img.naturalWidth, img.naturalHeight, ratio, offsetX, offsetY);
    const { width, height } = fitSize(sw, sh, maxEdge);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Image editing is not supported in this browser.");
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality),
    );
    if (!blob) throw new Error("Could not process that image.");
    return { blob, url: URL.createObjectURL(blob), width, height, bytes: blob.size };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
